import { selectSearchByQueryId } from 'ducks/searches/selectors'
import { createDoFetchInMemoryByQueryId } from 'ducks/searches/operations/createDoFetchInMemoryByQueryId'
import { doApiReadRequest, doApiWriteRequest } from 'ducks/requests/operations'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import { selectTimeframe } from 'ducks/reports/controls'
import { selectCurrentEntityById } from 'ducks/entities/selectors'
import { convertScatterIdToRaw } from 'util/scatterSwap'
import {
  CANCEL_DATA_EXPORT,
  CREATE_DATA_EXPORT,
  DOWNLOAD_DATA_EXPORT,
  FETCH_DATA_EXPORTS,
} from './types'
import {
  dataExportApiV0ResponseSchema,
  dataExportsApiV0ResponseSchema,
} from './schema'

const LOAD_ALL_EXPORTS_QUERYID = 'entityType:dataExport pageSize:10000'

export const doFetchExportsV0 = ({ skipLoaded } = {}) => (
  dispatch,
  getState
) => {
  const queryId = LOAD_ALL_EXPORTS_QUERYID
  const cursor = 'all'
  const state = getState()
  const { loaded = null, cursors = {} } = selectSearchByQueryId(state, queryId)
  const hasCurrentPage = !!cursors[cursor]
  const isStale = hasCurrentPage && cursors[cursor].isStale

  const hasLoaded = loaded && hasCurrentPage && !isStale

  if (hasLoaded && skipLoaded) {
    // Note we might need to change this in future to return the results
    // from the previous query
    return Promise.resolve({})
  }

  return dispatch(
    doApiReadRequest(
      FETCH_DATA_EXPORTS,
      'api/settings/exporting/exports',
      {},
      {
        normalizationSchema: dataExportsApiV0ResponseSchema,
        searches: {
          queryId,
          cursor,
          extractPagination: (
            _,
            {
              action: {
                transformedEntities: { dataExport: dataExportById = {} } = {},
              },
            }
          ) => {
            const nodes = Object.values(dataExportById)
            return {
              type: 'dataExport',
              nodes,
              startCursor: 1,
              endCursor: 1,
              hasNextPage: false,
              hasPreviousPage: false,
              totalCount: nodes.length,
              totalPageCount: 1,
            }
          },
        },
      }
    )
  )
}

export const doFetchExportsInMemory = createDoFetchInMemoryByQueryId({
  fromQueryId: LOAD_ALL_EXPORTS_QUERYID,
  entityType: 'dataExport',
  doLoadAllFn: doFetchExportsV0,
})

export const doCreateExportFromCurrentTimeframe = (options = {}) => (
  dispatch,
  getState
) => {
  const method = 'POST'
  const targetStores = ['pending', 'current']
  const entityType = 'dataExport'
  const state = getState()
  const currentUser = selectCurrentUser(state)
  const timeframe = selectTimeframe(state)
  const id = 'new'
  const optimistEntity = {
    id,
    created_at: Date.now(),
    state: 'requested',
    creator_id: currentUser.id,
  }

  return dispatch(
    doApiWriteRequest(
      CREATE_DATA_EXPORT,
      'api/settings/exporting/exports',
      {
        date_range_start: timeframe.start.toString(),
        date_range_end: timeframe.end.toString(),
      },
      {
        method,
        optimist: {
          entities: {
            [entityType]: {
              [id]: optimistEntity,
            },
          },
        },
        normalizationSchema: dataExportApiV0ResponseSchema,
        searches: {
          additionalActions: [
            {
              type: 'INVALIDATE_ENTITIES',
              entityTypes: ['dataExport'],
              phases: ['SUCCESS'],
            },
          ],
        },
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content: 'New export requested',
            },
            failed: {
              content: 'Export request failed',
              onClickAction: () => {
                dispatch(doCreateExportFromCurrentTimeframe(options))
              },
            },
          },
          entities: {
            additionalActions: [
              {
                entityType,
                entityId: id,
                stores: targetStores,
                operation: 'remove',
                phases: ['SUCCESS'],
              },
            ],
          },
        },
      }
    )
  )
}

export const doCancelOrDeleteExport = (id, options = {}) => (
  dispatch,
  getState
) => {
  const state = getState()
  const rawId = convertScatterIdToRaw(id)
  const currentEntity = selectCurrentEntityById(state, 'dataExport', id)
  const newEntityState =
    currentEntity.state === 'completed' ? 'deleted' : 'cancelled'
  const method = 'DELETE'
  const targetStores = ['pending', 'current']
  const entityType = 'dataExport'

  return dispatch(
    doApiWriteRequest(
      CANCEL_DATA_EXPORT,
      `api/settings/exporting/exports/${rawId}`,
      {},
      {
        method,
        optimist: {},
        // app: true,
        searches: {
          additionalActions: [
            {
              type: 'INVALIDATE_ENTITIES',
              entityTypes: ['dataExport'],
              phases: ['SUCCESS'],
            },
          ],
        },
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: false,
            },
            success: {
              enabled: true,
              content:
                newEntityState === 'cancelled'
                  ? 'Export cancelled'
                  : 'Export deleted',
            },
            failed: {
              content:
                newEntityState === 'cancelled'
                  ? 'Failed to cancel export'
                  : 'Failed to delete export',
              onClickAction: () => {
                dispatch(doCancelOrDeleteExport(id, options))
              },
            },
          },
          entities: {
            targetOperation: 'remove',
            additionalActions: [
              {
                entityType,
                entityId: id,
                stores: targetStores,
                operation: 'remove',
                phases: ['STARTED'],
              },
            ],
          },
        },
      }
    )
  )
}

export const doCancelOrDeleteExports = (ids, options = {}) => dispatch => {
  return Promise.all(
    ids.map(id => dispatch(doCancelOrDeleteExport(id, options)))
  )
}

export const doDownloadExport = id => dispatch => {
  const rawId = convertScatterIdToRaw(id)

  return dispatch(
    doApiReadRequest(
      DOWNLOAD_DATA_EXPORT,
      `api/settings/exporting/exports/${rawId}/download`,
      {
        moduleOptions: {
          toasts: {
            enabled: true,
            started: {
              enabled: true,
              content: 'Generating export download…',
            },
            success: {
              enabled: false,
            },
            failed: {
              content: 'Failed to download export',
              onClickAction: () => {
                dispatch(doDownloadExport(id))
              },
            },
          },
        },
      }
    )
  ).then(data => {
    window.open(data.url, '_blank')
  })
}
